import React, { createContext, useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { flowRight } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { TableInstance } from "rsuite/Table";
import { SortType } from "rsuite-table";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";
import { TypeAttributes } from "rsuite/esm/internals/types";
import {
    Button,
    Checkbox,
    Container,
    Content,
    Footer as RsuiteFooter,
    Header,
    Heading,
    HStack,
    IconButton,
    Message,
    Progress,
    SelectPicker,
    Stack,
    Table,
    Text,
    toaster,
} from "rsuite";

import SaveIcon from "@rsuite/icons/Save";
import EditIcon from "@rsuite/icons/Edit";
import HistoryIcon from "@rsuite/icons/History";
import GlobalIcon from "@rsuite/icons/Global";

import i18n from "utils/i18n";
import { HUB_NAMES } from "utils/serverless-hub";
import constants from "utils/constants";
import { getSorted, Paths } from "utils/sort-halper";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import {
    getTenantDisplayName,
    getTenantId,
    getTenantMoniker,
} from "store/environments/tenants/tenants-selectors";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import { Mailbox } from "store/environments/tenants/mailboxes/mailboxes-types";

import {
    getCreateOpen,
    setCreateOpen,
    getInProgress,
    setInProgress,
} from "store/environments/tenants/mailboxes/mailboxes-slices";

import {
    MailboxesWorkflow,
    MailboxesWorkflowStep,
    WorkflowStatus,
    WorkflowStepStatus,
} from "store/workflows/workflows-types";

import { getErrorMessage } from "api/defaults";
import {
    loadMailboxes,
    loadMailboxesWorkflows,
    updateMailboxes,
} from "api/environments/tenants/mailboxes-api";

import useRequestCancellation from "hooks/useRequestCancellation";
import useRefreshPage from "hooks/useRefreshPage";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";
import withWebSocket, {
    WithWebSocketProps,
} from "hocs/withWebSocket/withWebSocket";

import PopoverCell from "components/Common/PopoverCell/PopoverCell";
import EditableCell from "components/Common/EditableCell/EditableCell";
import Footer from "components/Common/Footer/Footer";

import History from "./History/History";
import ErrorLogs from "./ErrorLogs/ErrorLogs";
import Create from "./Create/Create";

import { formModel, ocrMethods } from "./mailboxes-helpers";

import "./Mailboxes.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithWebSocketProps {
    title?: string;

    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

export type TableItem<T> = T & {
    editing: boolean | null;
    errors?: Record<string, string> | null;
};

export type TableColumn = Paths<Mailbox>;

const columns: Record<
    keyof Omit<Mailbox, "id" | "creator" | "creationDate">,
    TableColumn
> = {
    name: "name",
    address: "address",
    disabled: "disabled",
    ocrMethod: "ocrMethod",
    company: "company.remoteId",
};

export const HistoryContext = createContext<TableItem<Mailbox> | null>(null);

const Mailboxes: React.FC<Props> = ({
    title = i18n.t("tenantConfig.captureMailboxes.header"),
    webSocketPayload,
    showLoader,
    showError,
    addWebSocketUserId,
    addWebSocketHabName,
}) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();

    const tableRef = useRef<TableInstance<any, any> | null>(null);

    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const activeTenantMoniker = useSelector((state: RootState): string =>
        getTenantMoniker(state)
    );

    const activeTenantName = useSelector((state: RootState): string =>
        getTenantDisplayName(state)
    );

    const createOpen = useSelector((state: RootState): boolean =>
        getCreateOpen(state)
    );

    const inProgress = useSelector((state: RootState): boolean =>
        getInProgress(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [, setMailboxes] = useImmer<ReadonlyArray<Mailbox>>([]);
    const [updatedMailboxes, setUpdatedMailboxes] = useImmer<Mailbox[]>([]);
    const [mailboxesWorkflows, setMailboxesWorkflows] = useImmer<
        MailboxesWorkflow[]
    >([]);

    const [updatePermitted, setUpdatePermitted] = useState<boolean>(true);

    const [activeWorkflow, setActiveWorkflow] =
        useImmer<MailboxesWorkflow | null>(null);

    const [mailboxesTableData, setMailboxesTableData] = useImmer<
        TableItem<Mailbox>[]
    >([]);
    const [selectedMailboxTableItem, setSelectedMailboxTableItem] =
        useState<TableItem<Mailbox> | null>(null);
    const [mailboxesSortColumn, setMailboxesSortColumn] = useState<TableColumn>(
        columns.company
    );
    const [mailboxesSortType, setMailboxesSortType] =
        useState<SortType>("desc");

    const [errorLogsOpen, setErrorLogsOpen] = useState<boolean>(false);
    const [historyLogsOpen, setHistoryLogsOpen] = useState<boolean>(false);

    const headerHeight = 40;
    const rowHeight = 46;

    const primaryBlue = "#17a1e5";
    const primaryYellow = "#edba3c";

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchMailboxes(), fetchMailboxesWorkflows()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchMailboxes = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadMailboxes(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setMailboxes(payload.data);
                if (mailboxesSortColumn && mailboxesSortType) {
                    setMailboxesTableData(
                        getSorted(
                            [
                                ...payload.data.map((sa) => ({
                                    ...sa,
                                    editing: false,
                                })),
                            ],
                            mailboxesSortColumn,
                            mailboxesSortType
                        )
                    );
                } else {
                    setMailboxesTableData([
                        ...payload.data.map((sa) => ({
                            ...sa,
                            editing: false,
                        })),
                    ]);
                }

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchMailboxesWorkflows = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadMailboxesWorkflows(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setMailboxesWorkflows(payload);

                return null;
            } else if (status === constants.statusCode.NotFound) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const submitMailboxes = async () => {
        try {
            const [payload, status] = await updateMailboxes(
                activeEnvironmentId,
                activeTenantId,
                {
                    id: activeTenantId,
                    name: activeTenantName,
                    moniker: activeTenantMoniker,
                    data: updatedMailboxes,
                },
                cancelTokenSource
            );

            if (status === constants.statusCode.OK && payload) {
                setMailboxesWorkflows((draft) => {
                    const workflow = draft.find((w) => w.id === payload.id);

                    if (workflow) {
                        Object.assign(workflow, payload);
                    } else {
                        draft.push({ ...payload, steps: [...payload.steps] });
                    }
                });

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const getCompletedStepsPrecent = (
        steps: ReadonlyArray<MailboxesWorkflowStep> | undefined
    ): number => {
        if (steps) {
            const all = steps.length;
            const completed = steps.filter(
                (s) =>
                    s.status === WorkflowStepStatus.Completed ||
                    s.status === WorkflowStepStatus.Failed
            ).length;

            return (completed / all) * 100;
        }

        return 0;
    };

    const getProgressStatement = (
        steps: ReadonlyArray<MailboxesWorkflowStep> | undefined
    ): string | null => {
        if (steps) {
            const all = steps.length;
            const completed = steps.filter(
                (s) =>
                    s.status === WorkflowStepStatus.Completed ||
                    s.status === WorkflowStepStatus.Failed
            ).length;

            return `${completed} / ${all}`;
        }

        return null;
    };

    const getProgressColor = (
        steps: ReadonlyArray<MailboxesWorkflowStep> | undefined
    ): string | undefined => {
        if (steps) {
            const pallet: Record<string, string> = {
                normal: primaryBlue,
                warning: primaryYellow,
            };

            return steps.some((s) => s.status === WorkflowStepStatus.Failed)
                ? pallet["warning"]
                : pallet["normal"];
        }
    };

    const progressPercent = getCompletedStepsPrecent(activeWorkflow?.steps);
    const progressStatement = getProgressStatement(activeWorkflow?.steps);
    const progressColor = getProgressColor(activeWorkflow?.steps);

    const handleOpenHistory = (tableItem: TableItem<Mailbox>) => {
        setSelectedMailboxTableItem(tableItem);
        setHistoryLogsOpen(true);
    };

    const handleOpenErrorLogs = () => {
        setErrorLogsOpen(true);
    };

    const handleOpenCreate = (open: boolean) => {
        dispatch(setCreateOpen(open));
    };

    const handleCreateCallback = (workflow: MailboxesWorkflow) => {
        setMailboxesWorkflows((draft) => {
            const item = draft.find((w) => w.id === workflow.id);

            if (item) {
                Object.assign(item, workflow);
            } else {
                draft.push({ ...workflow, steps: [...workflow.steps] });
            }
        });
    };

    const handleSortColumn = (
        sortColumn: string | undefined,
        sortType: SortType | undefined
    ) => {
        setMailboxesSortColumn(sortColumn as TableColumn);
        setMailboxesSortType(sortType as SortType);

        setMailboxesTableData((draft) =>
            getSorted(draft, sortColumn as TableColumn, sortType)
        );
    };

    const validateRow = (rowData: TableItem<Mailbox>) => {
        const result = formModel.check({
            name: rowData.name,
            address: rowData.address,
        });

        return {
            isValid: !result?.name?.hasError && !result?.address?.hasError,
            errors: {
                name: result?.name?.errorMessage ?? "",
                address: result?.address?.errorMessage ?? "",
            },
        };
    };

    const handleCheck = (checked: boolean, value: Mailbox) => {
        const updatedMailboxes = mailboxesTableData.map((mailbox) =>
            mailbox.id === value.id
                ? { ...mailbox, disabled: !mailbox.disabled }
                : mailbox
        );
        setMailboxesTableData(updatedMailboxes);
    };

    const handleEdit = (rowData: TableItem<Mailbox>) => {
        setMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                mailbox.editing = true;
            }
        });
    };

    const handleSave = (rowData: TableItem<Mailbox>) => {
        const { isValid } = validateRow(rowData);
        if (!isValid) {
            return;
        }

        setMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                mailbox.editing = false;
            }
        });

        setUpdatedMailboxes((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                Object.assign(mailbox, rowData);
            } else {
                draft.push({ ...rowData });
            }
        });
    };

    const handleRowDataChange = (rowData: Record<string, any>) => {
        const { isValid, errors } = validateRow(rowData as TableItem<Mailbox>);

        setMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                Object.assign(mailbox, rowData);

                mailbox.errors = isValid ? null : errors;
            }
        });
    };

    const handleAction = (mailbox: TableItem<Mailbox>) => {
        if (mailbox.editing) {
            handleSave(mailbox);
        } else {
            handleEdit(mailbox);
        }
    };

    const handleSubmit = () => {
        if (!updatePermitted) {
            return;
        }

        showLoader?.(true);

        Promise.all([submitMailboxes()]).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            setUpdatedMailboxes([]);

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.captureMailboxes.successMessage");
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                toastOptions
            );
        });
    };

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (mailboxesWorkflows) {
            addWebSocketHabName(HUB_NAMES.MAILBOXES_HANDLING);
            addWebSocketUserId(activeTenantId);
        }
    }, [activeTenantId, mailboxesWorkflows]);

    useEffect(() => {
        const workflow =
            mailboxesWorkflows.find(
                (w) => w.status === WorkflowStatus.InProgress
            ) ?? null;

        setActiveWorkflow(workflow);

        dispatch(setInProgress(workflow !== null));
    }, [mailboxesWorkflows]);

    useEffect(() => {
        const validated = mailboxesTableData.some(
            (item) => validateRow(item).isValid
        );

        const itemsSaved = mailboxesTableData.every((item) => !item.editing);

        const updated = updatedMailboxes.length > 0;

        const permitted = dataFetched && validated && itemsSaved && updated;

        if (updated) {
            dispatch(setIsUnsavedChangeAvailable(true));
        }

        setUpdatePermitted(permitted);
    }, [dataFetched, updatedMailboxes, mailboxesTableData]);

    useEffect(() => {
        if (webSocketPayload) {
            try {
                const payload = JSON.parse(
                    webSocketPayload
                ) as MailboxesWorkflow;

                if (payload) {
                    setMailboxesWorkflows((draft) => {
                        const index = draft.findIndex(
                            (w) => w.id === payload.id
                        );

                        if (index !== -1) {
                            draft[index] = {
                                ...payload,
                                steps: [...payload.steps],
                            };
                        } else {
                            draft.push({
                                ...payload,
                                steps: [...payload.steps],
                            });
                        }
                    });

                    if (payload.status === WorkflowStatus.Succeeded) {
                        if (
                            payload.steps.some(
                                (s) => s.status === WorkflowStepStatus.Failed
                            )
                        ) {
                            toaster.push(
                                <Message showIcon type={"warning"}>
                                    {t(
                                        "tenantConfig.captureMailboxes.warningMessage"
                                    )}
                                </Message>,
                                toastOptions
                            );
                        }

                        fetchData();
                    }
                }
            } catch (error) {
                console.error("Failed to parse payload:", error);
            }
        }
    }, [webSocketPayload]);

    return (
        dataFetched && (
            <Container className="mailboxes__container">
                <HistoryContext.Provider value={selectedMailboxTableItem}>
                    <History
                        open={historyLogsOpen}
                        setOpen={setHistoryLogsOpen}
                    />
                </HistoryContext.Provider>

                <ErrorLogs open={errorLogsOpen} setOpen={setErrorLogsOpen} />
                <Create
                    open={createOpen}
                    setOpen={handleOpenCreate}
                    callback={handleCreateCallback}
                />

                <Header>
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="mailboxes__content">
                    <Message
                        showIcon
                        type="warning"
                        className="mailboxes__message"
                    >
                        {t("tenantConfig.captureMailboxes.message")}
                    </Message>

                    <HStack
                        style={{ margin: "1rem 0" }}
                        justifyContent={"space-between"}
                        spacing={10}
                    >
                        <Stack.Item alignSelf={"center"} grow={1}>
                            {inProgress && (
                                <Progress.Line
                                    status={"active"}
                                    style={{ paddingLeft: "0" }}
                                    percent={progressPercent}
                                    strokeColor={progressColor}
                                    showInfo={false}
                                />
                            )}
                        </Stack.Item>

                        <Stack.Item alignSelf={"center"} grow={0.5}>
                            {inProgress && (
                                <Text weight="regular">
                                    {progressStatement}
                                </Text>
                            )}
                        </Stack.Item>

                        <Stack.Item alignSelf={"center"}>
                            <Button
                                style={{ paddingRight: "0", paddingLeft: "0" }}
                                appearance="link"
                                color="red"
                                onClick={() => handleOpenErrorLogs()}
                            >
                                <Text color="red">
                                    {t(`storageAccounts.errorLogs`)}
                                </Text>
                            </Button>
                        </Stack.Item>
                    </HStack>

                    <Table
                        ref={tableRef}
                        data={mailboxesTableData}
                        shouldUpdateScroll={false}
                        fillHeight={true}
                        headerHeight={headerHeight}
                        rowHeight={rowHeight}
                        sortColumn={mailboxesSortColumn}
                        sortType={mailboxesSortType}
                        onSortColumn={(dataKey, sortType) =>
                            handleSortColumn(dataKey, sortType)
                        }
                        className={
                            inProgress ? "mailboxes__table--readonly" : ""
                        }
                    >
                        <Table.Column width={100} fixed>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.table.active`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(rowData: TableItem<Mailbox>) => (
                                    <Checkbox
                                        checked={!rowData.disabled}
                                        onChange={(_, checked) => {
                                            handleCheck(checked, rowData);
                                        }}
                                        disabled={!rowData.editing}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column width={200} fixed>
                            <Table.HeaderCell>
                                {t(`tenantConfig.captureMailboxes.table.ocr`)}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={columns.ocrMethod}>
                                {(rowData: TableItem<Mailbox>) => (
                                    <SelectPicker
                                        className="mailboxes__table__column__ocr"
                                        disabled={!rowData.editing}
                                        searchable={false}
                                        placeholder={t(
                                            "tenantConfig.featureConfigurations.ocrConfigurations.placeholder"
                                        )}
                                        data={ocrMethods}
                                        value={rowData.ocrMethod}
                                        onChange={(value) =>
                                            handleRowDataChange({
                                                ...rowData,
                                                ocrMethod: value,
                                            })
                                        }
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column
                            flexGrow={3}
                            minWidth={200}
                            fixed
                            sortable
                        >
                            <Table.HeaderCell>
                                {t(`tenantConfig.captureMailboxes.table.name`)}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={columns.name}>
                                {(rowData: TableItem<Mailbox>) => (
                                    <EditableCell
                                        dataKey={columns.name}
                                        rowData={rowData}
                                        errorMessage={rowData.errors?.name}
                                        onChange={handleRowDataChange}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={3} minWidth={200} sortable>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.table.address`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={columns.address}>
                                {(rowData: TableItem<Mailbox>) => (
                                    <EditableCell
                                        dataKey={columns.address}
                                        rowData={rowData}
                                        errorMessage={rowData.errors?.address}
                                        onChange={handleRowDataChange}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={2} minWidth={200} sortable>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.table.companyAssignment`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={columns.company}>
                                {(rowData: TableItem<Mailbox>) => (
                                    <PopoverCell
                                        dataKey={columns.company}
                                        rowData={rowData}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.5} minWidth={80}>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.table.hyperlink`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey="hyperlink">
                                {(rowData: TableItem<Mailbox>) => (
                                    <a
                                        href={`${constants.mailboxUrl}${rowData.address}`}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        className="list__link"
                                    >
                                        <GlobalIcon />
                                    </a>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.5} minWidth={80}>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.mailboxCreation.action`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(rowData: TableItem<Mailbox>) => (
                                    <IconButton
                                        appearance="subtle"
                                        size="sm"
                                        onClick={() => {
                                            handleAction(rowData);
                                        }}
                                        icon={
                                            rowData.editing ? (
                                                <SaveIcon />
                                            ) : (
                                                <EditIcon />
                                            )
                                        }
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.5} minWidth={80}>
                            <Table.HeaderCell>
                                {t(
                                    `tenantConfig.captureMailboxes.table.history`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(rowData: TableItem<Mailbox>) => (
                                    <IconButton
                                        appearance="subtle"
                                        size="sm"
                                        onClick={() => {
                                            handleOpenHistory(rowData);
                                        }}
                                        icon={<HistoryIcon />}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>
                    </Table>
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={handleSubmit} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError, withWebSocket)(Mailboxes);

export default Component;
